import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Text,
  Button,
  Group,
  Badge,
  ActionIcon,
  Switch,
  LoadingOverlay,
  Card,
  Image,
  Stack,
  ThemeIcon,
  SimpleGrid,
  Center,
  Modal,
  Table,
  Avatar,
  ScrollArea,
} from "@mantine/core";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconEye,
  IconTrophy,
  IconTarget,
  IconUsers,
  IconClock,
  IconCalendar,
  IconPhoto,
  IconMedal,
  IconChartBar,
} from "@tabler/icons-react";
import { API_BASE_URL } from "../../config/baseUrls";

export default function MegaQuizManagement() {
  const [megaQuizzes, setMegaQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [bannerStatus, setBannerStatus] = useState(false);
  const [megaQuizStatus, setMegaQuizStatus] = useState(false);
  const [loadingBanner, setLoadingBanner] = useState(false);
  const [loadingMegaQuiz, setLoadingMegaQuiz] = useState(false);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [activeQuizTitle, setActiveQuizTitle] = useState("");
  const navigate = useNavigate();

  const fetchLeaderboard = async (quizId, title) => {
    setActiveQuizTitle(title);
    setLeaderboardOpen(true);
    setLoadingLeaderboard(true);
    setLeaderboardData([]);

    try {
      const token = localStorage.getItem("adminToken");
      // Adjust the URL to match your actual megaquiz route
      const response = await fetch(
        `${API_BASE_URL}/megaquiz/megaquizranking/${quizId}?page=1`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const result = await response.json();

      if (result.rankings) {
        setLeaderboardData(result.rankings);
      }
    } catch (err) {
      console.error("Failed to fetch leaderboard", err);
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  // --- LOGIC (UNTOUCHED) ---
  const fetchMegaQuizzes = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        navigate("/admin");
        return;
      }
      const response = await fetch(
        `${API_BASE_URL}/admin/mega-quizzes`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const result = await response.json();
      if (result.success) setMegaQuizzes(result.data);
      else setError("Failed to fetch mega quizzes");
    } catch (err) {
      setError("Network error.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMegaQuiz = async (megaQuizId, quizTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${quizTitle}"?`))
      return;
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/mega-quizzes/${megaQuizId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const result = await response.json();
      if (result.success) {
        setSuccess("Mega quiz deleted successfully!");
        setMegaQuizzes(
          megaQuizzes.filter((quiz) => quiz.megaQuizId !== megaQuizId),
        );
        setTimeout(() => setSuccess(""), 3000);
      } else setError("Failed to delete mega quiz");
    } catch (err) {
      setError("Network error.");
    }
  };

  const fetchBannerStatus = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/megabannerstatus`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const result = await response.json();
      if (result) setBannerStatus(result.bannerStatus.flagValue);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMegaQuizStatus = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/megaquizstatus`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const result = await response.json();
      if (result) setMegaQuizStatus(result.megaQuizStatus.flagValue);
    } catch (err) {
      console.error(err);
    }
  };

  const toggleBannerStatus = async () => {
    setLoadingBanner(true);
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/togglemegabanner`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.ok) {
        setBannerStatus(!bannerStatus);
        setSuccess("Banner status updated!");
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      setError("Failed to toggle banner");
    } finally {
      setLoadingBanner(false);
    }
  };

  const toggleMegaQuizStatus = async () => {
    setLoadingMegaQuiz(true);
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/togglemegaquiz`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.ok) {
        setMegaQuizStatus(!megaQuizStatus);
        setSuccess("Mega quiz status updated!");
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      setError("Failed to toggle mega quiz");
    } finally {
      setLoadingMegaQuiz(false);
    }
  };

  useEffect(() => {
    fetchMegaQuizzes();
    fetchBannerStatus();
    fetchMegaQuizStatus();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getQuizStatus = (startTime, endTime) => {
    const now = new Date();
    const start = startTime ? new Date(startTime) : null;
    const end = endTime ? new Date(endTime) : null;
    if (!start && !end) return { status: "Always Active", color: "green" };
    if (start && now < start) return { status: "Scheduled", color: "blue" };
    if (end && now > end) return { status: "Expired", color: "red" };
    return { status: "Active", color: "green" };
  };
  // --- LOGIC ENDS ---

  return (
    <Box>
      <Group justify="space-between" mb="lg">
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            Mega Quiz Events
          </Text>
          <Text size="sm" c="dimmed">
            Manage high-stakes quizzes and seasonal events
          </Text>
        </Box>
        <Group gap="xs">
          <Button
            variant="light"
            color="purple"
            radius="md"
            leftSection={<IconPhoto size={18} />}
            onClick={() =>
              navigate("/admin/dashboard/mega-quiz-management/banner-config")
            }
            style={{ transition: "transform 0.2s ease" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "translateY(-2px)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.transform = "translateY(0)")
            }
          >
            Configure Banner
          </Button>
          <Button
            leftSection={<IconPlus size={18} />}
            className="btn-ota-primary"
            onClick={() =>
              navigate("/admin/dashboard/mega-quiz-management/new")
            }
          >
            New Mega Quiz
          </Button>
        </Group>
      </Group>

      {/* Global Controls - FIXED ALIGNMENT */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} mb="xl">
        <Paper withBorder p="lg" radius="md" shadow="sm">
          <Group justify="space-between" align="center" wrap="nowrap">
            <Group gap="md">
              <ThemeIcon size="xl" radius="md" color="blue" variant="light">
                <IconTrophy size={24} />
              </ThemeIcon>
              <Box>
                <Text fw={700} size="md">
                  Mega Quiz Feature
                </Text>
                <Text size="xs" c="dimmed">
                  {megaQuizStatus ? "Module Enabled" : "Module Disabled"}
                </Text>
              </Box>
            </Group>
            <Switch
              size="lg"
              checked={megaQuizStatus}
              onChange={toggleMegaQuizStatus}
              disabled={loadingMegaQuiz}
              thumbIcon={loadingMegaQuiz ? <IconClock size={12} /> : null}
            />
          </Group>
        </Paper>

        <Paper withBorder p="lg" radius="md" shadow="sm">
          <Group justify="space-between" align="center" wrap="nowrap">
            <Group gap="md">
              <ThemeIcon size="xl" radius="md" color="purple" variant="light">
                <IconTarget size={24} />
              </ThemeIcon>
              <Box>
                <Text fw={700} size="md">
                  Homepage Banner
                </Text>
                <Text size="xs" c="dimmed">
                  {bannerStatus ? "Visible to Users" : "Hidden from Home"}
                </Text>
              </Box>
            </Group>
            <Switch
              size="lg"
              color="purple"
              checked={bannerStatus}
              onChange={toggleBannerStatus}
              disabled={loadingBanner}
              thumbIcon={loadingBanner ? <IconClock size={12} /> : null}
            />
          </Group>
        </Paper>
      </SimpleGrid>

      {/* Stats Cards */}
      <Grid mb="xl">
        {[
          {
            label: "Total Events",
            value: megaQuizzes.length,
            icon: IconTrophy,
            color: "purple",
          },
          {
            label: "Total Questions",
            value: megaQuizzes.reduce(
              (s, q) => s + (q._count?.questions || 0),
              0,
            ),
            icon: IconTarget,
            color: "blue",
          },
          {
            label: "Active Now",
            value: megaQuizzes.filter(
              (q) => getQuizStatus(q.startTime, q.endTime).status === "Active",
            ).length,
            icon: IconClock,
            color: "green",
          },
          {
            label: "Total Players",
            value: megaQuizzes.reduce(
              (s, q) => s + (q._count?.rankings || 0),
              0,
            ),
            icon: IconUsers,
            color: "orange",
          },
        ].map((stat, i) => (
          <Grid.Col span={{ base: 6, md: 3 }} key={i}>
            <Paper withBorder p="md" radius="md">
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    {stat.label}
                  </Text>
                  <Text fw={700} size="xl">
                    {stat.value}
                  </Text>
                </div>
                <ThemeIcon
                  variant="light"
                  color={stat.color}
                  radius="md"
                  size="lg"
                >
                  <stat.icon size={20} />
                </ThemeIcon>
              </Group>
            </Paper>
          </Grid.Col>
        ))}
      </Grid>

      {loading ? (
        <Stack align="center" h={200} justify="center">
          <LoadingOverlay
            visible={true}
            overlayProps={{ radius: "sm", blur: 2 }}
          />
        </Stack>
      ) : megaQuizzes.length === 0 ? (
        <Paper p="xl" withBorder radius="md" ta="center" bg="gray.0">
          <ThemeIcon size={60} radius="xl" color="gray" variant="light" mb="md">
            <IconTrophy size={30} />
          </ThemeIcon>
          <Text fw={600} size="lg">
            No Mega Quizzes
          </Text>
          <Button
            mt="md"
            onClick={() =>
              navigate("/admin/dashboard/mega-quiz-management/new")
            }
          >
            Create First Event
          </Button>
        </Paper>
      ) : (
        <Grid>
          {megaQuizzes.map((quiz) => {
            const status = getQuizStatus(quiz.startTime, quiz.endTime);
            return (
              <Grid.Col key={quiz.megaQuizId} span={{ base: 12, md: 6 }}>
                <Card
                  withBorder
                  padding="0"
                  radius="md"
                  className="hover:shadow-md transition-shadow"
                >
                  <Group align="stretch" gap={0}>
                    {/* Left: Image */}
                    <Box w={120} bg="gray.1" style={{ position: "relative" }}>
                      {quiz.coverImg ? (
                        <Image
                          src={quiz.coverImg}
                          h="100%"
                          w="100%"
                          fit="cover"
                        />
                      ) : (
                        <Center h="100%">
                          <IconTrophy size={32} color="gray" />
                        </Center>
                      )}
                    </Box>

                    {/* Right: Content */}
                    <Stack p="md" gap="xs" style={{ flex: 1 }}>
                      <Group justify="space-between" align="start">
                        <Box>
                          <Text fw={600} lineClamp={1}>
                            {quiz.titleEn}
                          </Text>
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            {quiz.titleJa}
                          </Text>
                        </Box>
                        <Badge color={status.color} variant="light">
                          {status.status}
                        </Badge>
                      </Group>

                      <Group gap="lg" mt="xs">
                        <Group gap={4}>
                          <IconTarget size={14} className="text-gray-400" />
                          <Text size="xs">
                            {quiz._count?.questions || 0} Qs
                          </Text>
                        </Group>
                        <Group gap={4}>
                          <IconUsers size={14} className="text-gray-400" />
                          <Text size="xs">
                            {quiz._count?.rankings || 0} Players
                          </Text>
                        </Group>
                      </Group>

                      <Group gap={4}>
                        <IconCalendar size={14} className="text-gray-400" />
                        <Text size="xs" c="dimmed">
                          {formatDate(quiz.startTime)} -{" "}
                          {formatDate(quiz.endTime)}
                        </Text>
                      </Group>

                      <Group mt="xs" justify="flex-end" gap="xs">
                        <Button
                          variant="light"
                          size="xs"
                          color="gray"
                          leftSection={<IconEye size={12} />}
                          onClick={() =>
                            navigate(
                              `/admin/dashboard/mega-quiz-management/preview/${quiz.megaQuizId}`,
                            )
                          }
                        >
                          Preview
                        </Button>

                        <Button
                          variant="light"
                          size="xs"
                          color="orange"
                          leftSection={<IconChartBar size={12} />}
                          onClick={() =>
                            navigate(
                              `/admin/dashboard/mega-quiz-management/leaderboard/${quiz.megaQuizId}`,
                              {
                                state: { quizTitle: quiz.titleEn },
                              },
                            )
                          }
                        >
                          Results
                        </Button>
                        <Button
                          variant="light"
                          size="xs"
                          color="blue"
                          leftSection={<IconEdit size={12} />}
                          onClick={() =>
                            navigate(
                              `/admin/dashboard/mega-quiz-management/edit/${quiz.megaQuizId}`,
                            )
                          }
                        >
                          Edit
                        </Button>
                        <ActionIcon
                          variant="light"
                          color="red"
                          size="md"
                          onClick={() =>
                            handleDeleteMegaQuiz(quiz.megaQuizId, quiz.titleEn)
                          }
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Stack>
                  </Group>
                </Card>
              </Grid.Col>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
